import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import ProgressBar from '../components/ProgressBar';
import API from '../services/api';
import { IconRoof } from '../components/Icons';
import { isDateOverdue } from '../utils/dateUtils';
function Reports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeProject, setActiveProject] = useState('all');

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await API.get('/reports/progress');
        setReport(res.data.report);
      } catch (err) {
        setError('Failed to load report data.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const countDelayed = (plots) =>
    (plots || []).filter(p => p.target_completion_date && p.status !== 'completed' && isDateOverdue(p.target_completion_date)).length;

  const getDisplayData = () => {
    if (!report) return null;
    if (activeProject === 'all') {
      const o = report.overall;
      const allPlots = (report.projects || []).flatMap(p => p.plots || []);
      return {
        total_plots: o.total_bungalows,
        completed: o.total_completed,
        active: o.total_active,
        delayed: countDelayed(allPlots),
        not_started: o.total_not_started,
        completion_rate: o.overall_avg_completion
      };
    }
    const proj = report.projects?.find(p => p.code === activeProject);
    if (!proj) return null;
    return {
      total_plots: proj.registered_plots,
      completed: proj.completed_plots,
      active: proj.active_plots,
      delayed: countDelayed(proj.plots),
      not_started: proj.not_started_plots,
      completion_rate: proj.avg_completion,
      stage_stats: proj.stage_stats,
      plots: proj.plots
    };
  };

  const displayData = getDisplayData();
  const skyoraData = report?.projects?.find(p => p.code === 'SKY');
  const mayavanData = report?.projects?.find(p => p.code === 'MAY');

  return (
    <div className="page">
      <Navbar />
      <main className="main-content">
        <div className="reports-page-header">
          <div>
            <h1 className="page-title">Compliance &amp; Progress Audits</h1>
            <p className="page-subtitle">View completion rates, stage-wise progress, and status breakdowns for all active and completed bungalows.</p>
          </div>
        </div>

        {loading && <div className="loading-spinner">Loading reports...</div>}
        {error && <div className="error-message">{error}</div>}

        {report && (
          <>
            <div className="report-tabs">
              <button className={`report-tab ${activeProject === 'all' ? 'active' : ''}`} onClick={() => setActiveProject('all')}>All Projects</button>
              <button className={`report-tab ${activeProject === 'SKY' ? 'active' : ''}`} onClick={() => setActiveProject('SKY')}>Skyora Karjat</button>
              <button className={`report-tab ${activeProject === 'MAY' ? 'active' : ''}`} onClick={() => setActiveProject('MAY')}>Sahyadri Sylvan Villas</button>
            </div>

            <div className="report-summary-grid">
              {(activeProject === 'all' || activeProject === 'SKY') && skyoraData && (
                <div className="report-summary-card">
                  <div className="report-summary-card-header">
                    <span className="report-summary-icon"><IconRoof size={20} /></span>
                    <h3>Skyora Karjat Project Summary</h3>
                  </div>
                  <div className="report-summary-row"><span>Total Bungalows</span><strong>{skyoraData.registered_plots} Units</strong></div>
                  <div className="report-summary-row"><span>Handover Finished</span><strong className="text-green">{skyoraData.completed_plots} Bungalows</strong></div>
                  <div className="report-summary-row"><span>Active Bungalows</span><strong>{skyoraData.active_plots} Bungalows</strong></div>
                  <div className="report-summary-row"><span>Not Started Bungalows</span><strong>{skyoraData.not_started_plots} Bungalows</strong></div>
                  <div className="report-summary-row"><span>Average Bungalow Completion</span><strong className="text-highlight">{skyoraData.avg_completion}% Completed</strong></div>
                </div>
              )}
              {(activeProject === 'all' || activeProject === 'MAY') && mayavanData && (
                <div className="report-summary-card">
                  <div className="report-summary-card-header">
                    <span className="report-summary-icon"><IconRoof size={20} /></span>
                    <h3>Sahyadri Sylvan Villas Project Summary</h3>
                  </div>
                  <div className="report-summary-row"><span>Total Bungalows</span><strong>{mayavanData.registered_plots} Units</strong></div>
                  <div className="report-summary-row"><span>Handover Finished</span><strong className="text-green">{mayavanData.completed_plots} Bungalows</strong></div>
                  <div className="report-summary-row"><span>Active Bungalows</span><strong>{mayavanData.active_plots} Bungalows</strong></div>
                  <div className="report-summary-row"><span>Not Started Bungalows</span><strong>{mayavanData.not_started_plots} Bungalows</strong></div>
                  <div className="report-summary-row"><span>Average Bungalow Completion</span><strong className="text-highlight">{mayavanData.avg_completion}% Completed</strong></div>
                </div>
              )}
            </div>

            {activeProject !== 'all' && displayData && (
              <>
                <div className="report-overview">
                  <div className="report-stat-block">
                    <div className="report-stat-value">{displayData.total_plots || 0}</div>
                    <div className="report-stat-label">Total Bungalows</div>
                  </div>
                  <div className="report-stat-block report-stat-block--green">
                    <div className="report-stat-value">{displayData.completed || 0}</div>
                    <div className="report-stat-label">Completed</div>
                  </div>
                  <div className="report-stat-block report-stat-block--amber">
                    <div className="report-stat-value">{displayData.active || 0}</div>
                    <div className="report-stat-label">Active</div>
                  </div>
                  <div className="report-stat-block report-stat-block--red">
                    <div className="report-stat-value">{displayData.delayed || 0}</div>
                    <div className="report-stat-label">Delayed</div>
                  </div>
                  <div className="report-stat-block report-stat-block--gray">
                    <div className="report-stat-value">{displayData.not_started || 0}</div>
                    <div className="report-stat-label">Not Started</div>
                  </div>
                </div>
                <div className="report-completion-section">
                  <div className="report-completion-header">
                    <h2 className="section-title">Average Completion</h2>
                    <span className="report-completion-percent">{displayData.completion_rate || 0}%</span>
                  </div>
                  <ProgressBar percentage={displayData.completion_rate || 0} color="#009966" height="10px" />
                </div>
              </>
            )}

          </>
        )}
      </main>
    </div>
  );
}

export default Reports;
